import { useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { ReactComponent as CloseIcon } from "../../assets/icons/close.svg";
import "./TranslationPopover.css";

const VIEWPORT_MARGIN = 12;
const POPOVER_MAX_HEIGHT = 360;

/**
 * Keep the panel fully inside the viewport after content/size changes.
 * `position` is a preferred anchor (usually near the selection).
 */
const clampPopoverToViewport = (el, preferred) => {
  if (!el || typeof window === "undefined") return;

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const maxHeight = Math.min(POPOVER_MAX_HEIGHT, vh - VIEWPORT_MARGIN * 2);
  el.style.maxHeight = `${maxHeight}px`;

  // Measure after applying max-height so scrollable body is accurate.
  const width = el.offsetWidth;
  const height = el.offsetHeight;

  let left = Number(preferred?.x) || VIEWPORT_MARGIN;
  let top = Number(preferred?.y) || VIEWPORT_MARGIN;

  left = Math.min(
    Math.max(VIEWPORT_MARGIN, left),
    Math.max(VIEWPORT_MARGIN, vw - width - VIEWPORT_MARGIN),
  );

  // If it would overflow the bottom, try placing above the selection instead.
  if (top + height > vh - VIEWPORT_MARGIN) {
    const anchorTop = Number(preferred?.anchorTop);
    if (Number.isFinite(anchorTop)) {
      const above = anchorTop - height - 8;
      if (above >= VIEWPORT_MARGIN) {
        top = above;
      } else {
        top = Math.max(VIEWPORT_MARGIN, vh - height - VIEWPORT_MARGIN);
      }
    } else {
      top = Math.max(VIEWPORT_MARGIN, vh - height - VIEWPORT_MARGIN);
    }
  }

  top = Math.min(
    Math.max(VIEWPORT_MARGIN, top),
    Math.max(VIEWPORT_MARGIN, vh - height - VIEWPORT_MARGIN),
  );

  el.style.left = `${left}px`;
  el.style.top = `${top}px`;
};

/**
 * Minimal translation result card shown near the selection.
 * Stays inside the viewport and scrolls long source/result text.
 */
const TranslationPopover = ({
  isVisible,
  position,
  sourceText,
  translatedText,
  isLoading,
  error,
  onClose,
}) => {
  const { t } = useTranslation();
  const panelRef = useRef(null);

  useLayoutEffect(() => {
    if (!isVisible) return undefined;

    const el = panelRef.current;
    if (!el) return undefined;

    clampPopoverToViewport(el, position);

    const handleReposition = () => clampPopoverToViewport(el, position);
    window.addEventListener("resize", handleReposition);
    return () => {
      window.removeEventListener("resize", handleReposition);
    };
  }, [
    isVisible,
    position,
    sourceText,
    translatedText,
    isLoading,
    error,
  ]);

  useEffect(() => {
    if (!isVisible) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };

    const handlePointerDown = (event) => {
      if (panelRef.current?.contains(event.target)) return;
      onClose?.();
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handlePointerDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  // Rough initial coords; useLayoutEffect measures and clamps before paint.
  const fallbackLeft = Number(position?.x) || VIEWPORT_MARGIN;
  const fallbackTop = Number(position?.y) || VIEWPORT_MARGIN;

  return createPortal(
    <div
      ref={panelRef}
      className="translation-popover"
      style={{ left: `${fallbackLeft}px`, top: `${fallbackTop}px` }}
      role="dialog"
      aria-label={t("contextMenu.translationTitle", "Translation")}
    >
      <div className="translation-popover-header">
        <span className="translation-popover-title">
          {t("contextMenu.translationTitle", "Translation")}
        </span>
        <button
          type="button"
          className="translation-popover-close"
          onClick={onClose}
          aria-label={t("common.close", "Close")}
        >
          <CloseIcon width={16} height={16} aria-hidden="true" />
        </button>
      </div>
      <div className="translation-popover-body">
        {sourceText ? (
          <p className="translation-popover-source">{sourceText}</p>
        ) : null}
        {isLoading ? (
          <p className="translation-popover-status" role="status">
            {t("contextMenu.translating", "Translating…")}
            <span
              className="translation-popover-spinner"
              aria-hidden="true"
            />
          </p>
        ) : null}
        {error ? (
          <p className="translation-popover-error" role="alert">
            {error}
          </p>
        ) : null}
        {!isLoading && !error && translatedText ? (
          <p className="translation-popover-result">{translatedText}</p>
        ) : null}
      </div>
    </div>,
    document.body,
  );
};

export default TranslationPopover;
